import type { Identifier } from "ra-core";

export type StatsScope = "day" | "week" | "month";

export interface SetterStats {
  id: Identifier;
  first_name: string;
  last_name?: string | null;
  administrator: boolean;
  calls: number;
  /** Calls where the lead answered (contacted or recontact) */
  talked: number;
  first_contact: number;
  second_contact: number;
  sure_purchase: number;
  recontact: number;
  lost: number;
  no_answer: number;
  sales: number;
  /** Welcome calls made after an installation */
  welcome_calls: number;
  /** Maintenance plans closed after their welcome call */
  maintenances: number;
  points: number;
  /** Leads that got this person as owner in the period */
  leads_taken: number;
  /** Hours from the lead arriving to this person's first call, averaged */
  avg_first_call_hours: number | null;
  active_days: number;
  /** Euros owed for the period, from the commission ledger */
  commission: number;
}

export interface CallLogRow {
  id: Identifier;
  created_at: string;
  day: string;
  sales_id: Identifier;
  client_id?: Identifier | null;
  outcome: string;
  stage?: string | null;
  points: number;
  is_call: boolean;
  first_name?: string | null;
  last_name?: string | null;
  city?: string | null;
}

export const STATS_NUMBERS: (keyof SetterStats)[] = [
  "calls",
  "talked",
  "first_contact",
  "second_contact",
  "sure_purchase",
  "recontact",
  "lost",
  "no_answer",
  "sales",
  "welcome_calls",
  "maintenances",
  "points",
  "leads_taken",
  "active_days",
  "commission",
];

const TIME_ZONE = "Europe/Madrid";

/** YYYY-MM-DD of a date in Madrid. */
export const madridDay = (date: Date): string =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);

const toDate = (day: string) => new Date(`${day}T12:00:00Z`);
const fromDate = (date: Date) => date.toISOString().slice(0, 10);
const addDays = (day: string, delta: number) => {
  const date = toDate(day);
  date.setUTCDate(date.getUTCDate() + delta);
  return fromDate(date);
};

export interface Period {
  scope: StatsScope;
  /** YYYY-MM-DD, inclusive */
  from: string;
  to: string;
}

/** The period that contains a day: the day, its Monday–Sunday week or its month. */
export const periodOf = (scope: StatsScope, day: string): Period => {
  if (scope === "day") return { scope, from: day, to: day };
  if (scope === "week") {
    const weekday = (toDate(day).getUTCDay() + 6) % 7; // Monday = 0
    const from = addDays(day, -weekday);
    return { scope, from, to: addDays(from, 6) };
  }
  const from = `${day.slice(0, 7)}-01`;
  const next = toDate(from);
  next.setUTCMonth(next.getUTCMonth() + 1);
  return { scope, from, to: addDays(fromDate(next), -1) };
};

/** The previous or next period of the same size. */
export const shiftPeriod = (period: Period, delta: 1 | -1): Period => {
  if (period.scope === "day")
    return periodOf("day", addDays(period.from, delta));
  if (period.scope === "week")
    return periodOf("week", addDays(period.from, 7 * delta));
  const date = toDate(period.from);
  date.setUTCMonth(date.getUTCMonth() + delta);
  return periodOf("month", fromDate(date));
};

const MONTHS = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
];

const shortDay = (day: string) =>
  `${Number(day.slice(8, 10))} ${MONTHS[Number(day.slice(5, 7)) - 1].slice(0, 3)}`;

export const periodLabel = (period: Period, today: string): string => {
  if (period.scope === "day") {
    if (period.from === today) return "Hoy";
    if (period.from === addDays(today, -1)) return "Ayer";
    return shortDay(period.from);
  }
  if (period.scope === "week") {
    const current = periodOf("week", today);
    return current.from === period.from
      ? "Esta semana"
      : `Semana del ${shortDay(period.from)} al ${shortDay(period.to)}`;
  }
  const month = MONTHS[Number(period.from.slice(5, 7)) - 1];
  return period.from.slice(0, 4) === today.slice(0, 4)
    ? month
    : `${month} ${period.from.slice(0, 4)}`;
};

/** Sales closed out of the leads talked to, as a percentage. */
export const closingRate = (stats: Pick<SetterStats, "sales" | "talked">) =>
  stats.talked ? Math.round((stats.sales / stats.talked) * 100) : null;

/** Setters first (by points), then the admins who also sell. */
export const sortStats = (rows: SetterStats[]) =>
  [...rows].sort(
    (a, b) =>
      Number(a.administrator) - Number(b.administrator) ||
      b.points - a.points ||
      b.calls - a.calls,
  );

/** Hides people with nothing in the period unless they are setters. */
export const visibleStats = (rows: SetterStats[]) =>
  rows.filter(
    (row) => !row.administrator || row.calls || row.sales || row.leads_taken,
  );

export const OUTCOME_LABEL: Record<string, string> = {
  sale: "Venta",
  contacted: "Hablado",
  recontact: "Recontacto",
  lost: "No compra",
  no_answer: "No contesta",
  welcome: "Bienvenida",
  maintenance: "Mantenimiento",
};

export const STAGE_LABEL: Record<string, string> = {
  first_contact: "1er contacto",
  second_contact: "2º contacto",
  sure_purchase: "Compra segura",
  recontact: "Recontactar",
  no_purchase: "No compra",
  purchased: "Compra",
  no_answer: "No contesta",
  welcome: "Bienvenida",
};

/** Stages that say the same as the outcome: no need to repeat them */
const STAGE_IS_OUTCOME: Record<string, string> = {
  purchased: "sale",
  no_purchase: "lost",
  recontact: "recontact",
  no_answer: "no_answer",
  welcome: "welcome",
  maintenance: "maintenance",
};

/** "Hablado · 1er contacto" */
export const logLabel = (row: Pick<CallLogRow, "outcome" | "stage">) => {
  const outcome = OUTCOME_LABEL[row.outcome] ?? row.outcome;
  const stage =
    row.stage && STAGE_IS_OUTCOME[row.stage] !== row.outcome
      ? STAGE_LABEL[row.stage]
      : null;
  return stage ? `${outcome} · ${stage}` : outcome;
};

/** Calls per day of the period, for the small bars. */
export const callsByDay = (rows: CallLogRow[], period: Period) => {
  const days: { day: string; calls: number; talked: number; sales: number }[] =
    [];
  for (let day = period.from; day <= period.to; day = addDays(day, 1)) {
    const inDay = rows.filter((row) => row.day === day);
    days.push({
      day,
      calls: inDay.filter((row) => row.is_call).length,
      talked: inDay.filter((row) =>
        ["contacted", "recontact"].includes(row.outcome),
      ).length,
      sales: inDay.filter((row) => row.outcome === "sale").length,
    });
  }
  return days;
};
